import React from "react";
import { Link } from "react-router-dom";
import {
  Pickaxe, Home, BookOpen, UserPlus, Building2, ShieldCheck, FileText,
  ClipboardList, Megaphone, Briefcase, Users, BarChart3, Shield, Download,
} from "lucide-react";

const SECTIONS = [
  {
    role: "Getting Started",
    icon: UserPlus,
    items: [
      "Click Create Account on the welcome page and choose SMME Supplier or Mine Buyer.",
      "SMMEs register with name, surname and email. Buyers also pick their mine (Black Rock, Khumani or Beeshoek).",
      "Access is granted immediately after registration — no approval wait.",
      "Use the Home button on any screen to return to this welcome page, and Sign Out to log out.",
    ],
  },
  {
    role: "SMME — Company Profile",
    icon: Building2,
    items: [
      "Open Company Profile and complete your contact person, company details, Tax & Registration numbers and BEE level.",
      "Under Location, tick your District, then choose a Municipality and your Town/Village.",
      "Pick up to two Business Sectors that best describe your company.",
      "Click Save Profile. Buyers search this information, so keep it accurate.",
    ],
  },
  {
    role: "SMME — Compliance Documents",
    icon: ShieldCheck,
    items: [
      "Upload SARS, BEE and COIDA certificates (add an expiry date for each), plus Company Profile, Registration and Proof of Address.",
      "Colour tags show status: green = valid, amber = expiring within 30 days, red = expired.",
      "Use Replace to update a document or Delete to remove it. The completeness bar tracks your progress.",
      "You will receive automatic email reminders before SARS, BEE or COIDA certificates expire.",
    ],
  },
  {
    role: "SMME — Opportunities & Responses",
    icon: FileText,
    items: [
      "Browse posted opportunities (RFQ, Tender, RFI, RFP) and filter by type, district or sector.",
      "Click Respond to submit a message, or Auto for a quick one-tap response.",
      "Download any attached specification directly from the opportunity card.",
      "Track progress in My Responses (Submitted, Under Review, Shortlisted, Awarded, Not Successful).",
    ],
  },
  {
    role: "Buyer — Posting & Sourcing",
    icon: Briefcase,
    items: [
      "Use Post Opportunity to publish an RFQ/Tender/RFI/RFP with a closing date and optional attachment.",
      "Target a specific district and sector, or tick Broadcast to all SMMEs — matching suppliers are emailed automatically.",
      "Open SMME Database to search registered suppliers by company, district, town, sector or BEE level, and view their documents.",
      "Open any opportunity to View Responses from suppliers.",
    ],
  },
  {
    role: "Buyer — Reports & Notices",
    icon: BarChart3,
    items: [
      "Reports shows total registered SMMEs, opportunities posted (and by whom), and supplier breakdowns by district and sector.",
      "Post to the Notice Board (Meetings, Announcements, Proposals) with district/sector targeting.",
      "Notices can be broadcast to all SMMEs or limited to a matching district and sector.",
    ],
  },
  {
    role: "Administrator",
    icon: Shield,
    items: [
      "Admins have full access to every area of the portal.",
      "Manage User Accounts — view all users and delete invalid registrations (admin accounts are protected).",
      "Access the full SMME Database, all Reports, Opportunities and the Notice Board.",
      "Post opportunities and notices on behalf of the portal.",
    ],
  },
];

export default function UserManual() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="manual-brand">
            <img src="/assmang-logo.png" alt="Assmang logo" className="h-9 w-auto" />
            <span className="hidden sm:inline font-display font-extrabold text-slate-900 tracking-tight">ASSMANG SMME PORTAL</span>
          </Link>
          <div className="flex items-center gap-2">
            <a href="/assmang-smme-portal-user-manual.pdf" download data-testid="manual-download-pdf" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#00427A] border border-slate-200 hover:border-[#00427A] rounded-md px-3 py-1.5 transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <Link to="/" data-testid="manual-home-button" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#00427A] border border-slate-200 hover:border-[#00427A] rounded-md px-3 py-1.5 transition-colors">
              <Home className="h-4 w-4" /> Home
            </Link>
            <Link to="/login" className="text-sm font-semibold bg-[#00427A] text-white rounded-md px-4 py-2 hover:bg-[#00325C] transition-colors">Sign In</Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden bg-[#1E252B]">
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 py-16">
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#FCD34D] font-semibold mb-4 flex items-center gap-2">
            <BookOpen className="h-4 w-4" /> User Manual
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">How to use the portal</h1>
          <p className="text-slate-300 mt-4 max-w-2xl">A step-by-step guide for SMME suppliers, mine buyers and administrators.</p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-14 space-y-8">
        {SECTIONS.map((s, i) => (
          <div key={s.role} data-testid={`manual-section-${i}`} className="bg-white rounded-xl border border-slate-200 p-7 animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 rounded-lg bg-[#00427A]/10 flex items-center justify-center">
                <s.icon className="h-5 w-5 text-[#00427A]" />
              </div>
              <h2 className="font-display text-xl font-bold text-slate-900">{s.role}</h2>
            </div>
            <ol className="space-y-3">
              {s.items.map((it, idx) => (
                <li key={idx} className="flex gap-3 text-slate-700">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-[#F2B50C]/10 text-[#F2B50C] text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                  <span className="text-sm leading-relaxed pt-0.5">{it}</span>
                </li>
              ))}
            </ol>
          </div>
        ))}

        <div className="bg-[#00427A] rounded-xl p-8 text-center">
          <h3 className="font-display text-2xl font-black text-white">Ready to get started?</h3>
          <p className="text-blue-100 mt-2">Create your account and access opportunities today.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link to="/register" className="inline-flex items-center gap-2 bg-[#F2B50C] hover:bg-[#D99E00] text-[#0A2540] font-semibold px-6 py-3 rounded-md transition-colors">
              <UserPlus className="h-4 w-4" /> Create Account
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-md border border-white/20 transition-colors">
              <Home className="h-4 w-4" /> Back to Home
            </Link>
          </div>
        </div>
      </main>

      <footer className="bg-[#1E252B] text-slate-400 py-8">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex items-center gap-2">
          <Pickaxe className="h-5 w-5 text-[#F2B50C]" />
          <span className="font-display font-bold text-white">ASSMANG SMME PORTAL</span>
        </div>
      </footer>
    </div>
  );
}
